import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Cable, Plus, Pencil, Trash2, Save, ImagePlus, ZoomIn, ZoomOut, Maximize2, Search, X } from "lucide-react";

const EMPTY = { model: "", ecu: "", mcu: "" };
const inputCls = "w-full h-9 rounded-md bg-[#15171d] border border-zinc-700/70 px-3 text-sm text-zinc-100 outline-none focus:border-red-500";

const PinEditor = ({ initial, onSaved, onCancel }) => {
  const [f, setF] = useState(initial || EMPTY);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.model.trim()) return toast.error("Model is required");
    if (!f.id && !file) return toast.error("Pin photo is required");
    const fd = new FormData();
    ["model", "ecu", "mcu"].forEach((k) => fd.append(k, f[k] || ""));
    if (file) fd.append("file", file);
    setBusy(true);
    try {
      const res = f.id ? await api.put(`/mcu-pinouts/${f.id}`, fd) : await api.post("/mcu-pinouts", fd);
      toast.success(f.id ? "Pinout updated" : "Pinout added");
      onSaved(res.data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to save"); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="rounded-lg border border-red-900/40 bg-[#0f1116] p-4 space-y-3" data-testid="mcu-pinout-editor">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div><label className="text-[11px] uppercase tracking-wider text-zinc-400">Type / Model *</label>
          <input className={inputCls} value={f.model} onChange={(e) => set("model", e.target.value)} placeholder="Scoopy K2F / Genio" data-testid="mcu-pinout-model-input" /></div>
        <div><label className="text-[11px] uppercase tracking-wider text-zinc-400">ECU Part Number</label>
          <input className={inputCls} value={f.ecu} onChange={(e) => set("ecu", e.target.value)} placeholder="30400-K0J-N01 / 30400-K0J-N11" data-testid="mcu-pinout-ecu-input" /></div>
        <div><label className="text-[11px] uppercase tracking-wider text-zinc-400">MCU</label>
          <input className={inputCls} value={f.mcu} onChange={(e) => set("mcu", e.target.value)} placeholder="R7F701021" data-testid="mcu-pinout-mcu-input" /></div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <label className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-zinc-700/70 bg-[#15171d] text-sm text-zinc-300 cursor-pointer hover:border-red-500">
          <ImagePlus size={15} /> {file ? file.name : f.id ? "Replace pin photo" : "Pin photo *"}
          <input type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="mcu-pinout-image-input" />
        </label>
        <div className="ml-auto flex gap-2">
          <button type="button" onClick={onCancel} className="h-9 px-4 rounded-md border border-zinc-700/70 text-sm text-zinc-300 hover:bg-white/5" data-testid="mcu-pinout-cancel">Cancel</button>
          <button type="submit" disabled={busy} className="h-9 px-4 rounded-md bg-[#e52321] hover:bg-[#c51717] text-white text-sm font-bold inline-flex items-center gap-2 disabled:opacity-50" data-testid="mcu-pinout-save">
            <Save size={15} /> {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </form>
  );
};

const ZoomImage = ({ src, alt }) => {
  const [z, setZ] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const drag = useRef(null);
  const pts = useRef(new Map());
  const pinch = useRef(null);
  const boxRef = useRef(null);
  const clamp = (v) => Math.min(8, Math.max(1, v));
  const reset = () => { setZ(1); setPos({ x: 0, y: 0 }); };
  useEffect(() => { reset(); }, [src]);
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const onWheel = (e) => { e.preventDefault(); setZ((p) => clamp(p * (e.deltaY < 0 ? 1.15 : 1 / 1.15))); };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);
  const dist = () => { const [a, b] = [...pts.current.values()]; return Math.hypot(a.x - b.x, a.y - b.y); };
  const down = (e) => {
    if (e.target.closest("button")) return;
    pts.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    e.currentTarget.setPointerCapture(e.pointerId);
    if (pts.current.size === 2) { pinch.current = { d: dist(), z }; drag.current = null; return; }
    drag.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
  };
  const move = (e) => {
    if (!pts.current.has(e.pointerId)) return;
    pts.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pts.current.size >= 2 && pinch.current) { setZ(clamp(pinch.current.z * (dist() / pinch.current.d))); return; }
    if (drag.current && z > 1) setPos({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y });
  };
  const up = (e) => {
    pts.current.delete(e.pointerId);
    if (pts.current.size < 2) pinch.current = null;
    if (pts.current.size === 0) drag.current = null;
    else { const p = [...pts.current.values()][0]; drag.current = { x: p.x - pos.x, y: p.y - pos.y }; }
  };
  const Btn = ({ onClick, children, tid }) => (
    <button type="button" onClick={onClick} data-testid={tid}
      className="h-9 w-9 rounded-md bg-black/70 border border-zinc-700 text-zinc-200 hover:bg-[#e52321] hover:border-[#e52321] active:bg-[#e52321] inline-flex items-center justify-center">{children}</button>
  );
  return (
    <div ref={boxRef} className="relative w-full flex-1 min-h-[320px] rounded-md border border-zinc-800 bg-black overflow-hidden select-none"
      style={{ cursor: z > 1 ? (drag.current ? "grabbing" : "grab") : "zoom-in", touchAction: "none" }}
      onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
      onDoubleClick={(e) => { if (!e.target.closest("button")) { if (z > 1) reset(); else setZ(2.5); } }} data-testid="mcu-pinout-zoom-box">
      <img src={src} alt={alt} draggable={false} className="absolute inset-0 w-full h-full object-contain"
        style={{ transform: `translate(${pos.x}px, ${pos.y}px) scale(${z})`, transition: drag.current || pinch.current ? "none" : "transform 80ms ease-out" }} data-testid="mcu-pinout-image" />
      <div className="absolute right-2 top-2 flex flex-col gap-1.5">
        <Btn onClick={() => setZ((p) => clamp(p * 1.3))} tid="mcu-pinout-zoom-in"><ZoomIn size={16} /></Btn>
        <Btn onClick={() => setZ((p) => clamp(p / 1.3))} tid="mcu-pinout-zoom-out"><ZoomOut size={16} /></Btn>
        <Btn onClick={reset} tid="mcu-pinout-zoom-reset"><Maximize2 size={16} /></Btn>
      </div>
      <div className="absolute left-2 bottom-2 px-2 py-0.5 rounded bg-black/70 text-[11px] font-mono text-zinc-300" data-testid="mcu-pinout-zoom-level">{Math.round(z * 100)}%</div>
    </div>
  );
};

export const McuPinoutTab = () => {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [selId, setSelId] = useState("");
  const [editor, setEditor] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const pick = (x) => { setSelId(x.id); setQ(""); setOpen(false); setEditor(null); };
  const filtered = items.filter((x) => {
    const s = q.trim().toLowerCase();
    return !s || [x.model, x.ecu, x.mcu].some((v) => (v || "").toLowerCase().includes(s));
  });
  const load = useCallback(async () => {
    try { const r = await api.get("/mcu-pinouts"); setItems(r.data); }
    catch { toast.error("Failed to load pinouts"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const sel = items.find((x) => x.id === selId);
  const onSaved = (d) => { setEditor(null); load().then(() => setSelId(d.id)); };
  const del = async () => {
    if (!sel || !window.confirm(`Delete pinout "${sel.model}"?`)) return;
    try { await api.delete(`/mcu-pinouts/${sel.id}`); toast.success("Deleted"); setSelId(""); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="h-full p-2 sm:p-3" data-testid="mcu-pinout-tab">
      <div className="h-full rounded-lg border border-red-900/40 bg-[#0f1116] flex flex-col">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-zinc-800/80">
          <Cable size={16} className="text-red-400" />
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-red-400">ECU Pinout</h3>
          <span className="ml-auto text-[11px] text-zinc-500">{items.length} type{items.length === 1 ? "" : "s"}</span>
        </div>
        <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-3 p-3 items-stretch">
          <div className="rounded-md border border-zinc-800 bg-[#12141a] p-3 space-y-3 self-start" data-testid="mcu-pinout-side">
            <div className="relative" data-testid="mcu-pinout-combo">
              <label className="text-[11px] uppercase tracking-wider text-zinc-400">Type</label>
              <div className="relative mt-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
                <input value={open ? q : (sel ? `${sel.model}${sel.mcu ? ` — ${sel.mcu}` : ""}` : q)}
                  onChange={(e) => { setQ(e.target.value); setOpen(true); }}
                  onFocus={() => { setQ(""); setOpen(true); }}
                  onBlur={() => setTimeout(() => setOpen(false), 150)}
                  onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); if (e.key === "Enter" && filtered.length) pick(filtered[0]); }}
                  placeholder="Search type / ECU part no / MCU…"
                  className={`${inputCls} h-10 pl-9 pr-8`} data-testid="mcu-pinout-select" />
                {(sel || q) && !open && (
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { setSelId(""); setQ(""); setEditor(null); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-red-400" data-testid="mcu-pinout-search-clear"><X size={14} /></button>
                )}
              </div>
              {open && (
                <div className="absolute z-20 left-0 right-0 mt-1 max-h-64 overflow-y-auto rounded-md border border-zinc-700 bg-[#15171d] shadow-xl" data-testid="mcu-pinout-results">
                  {filtered.length === 0 && <div className="px-3 py-2 text-xs text-zinc-500">No results</div>}
                  {filtered.map((x) => (
                    <button key={x.id} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(x)}
                      className={`w-full text-left px-3 py-2 border-b border-zinc-800/60 last:border-0 hover:bg-[#e52321]/20 ${x.id === selId ? "bg-[#e52321]/15" : ""}`}
                      data-testid={`mcu-pinout-result-${x.id}`}>
                      <div className="text-sm text-zinc-100 font-semibold">{x.model}{x.mcu ? <span className="text-zinc-400 font-normal"> — {x.mcu}</span> : null}</div>
                      {x.ecu && <div className="text-[11px] font-mono text-red-300 truncate">{x.ecu}</div>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {sel && (
              <div className="space-y-1 text-xs">
                <div className="text-[11px] uppercase tracking-wider text-zinc-400">ECU</div>
                <div className="font-mono text-red-300 break-words" data-testid="mcu-pinout-detail-ecu">{sel.ecu || "—"}</div>
                <div className="text-[11px] uppercase tracking-wider text-zinc-400 pt-1">MCU</div>
                <div className="font-mono text-zinc-200">{sel.mcu || "—"}</div>
              </div>
            )}
            {isAdmin && (
              <div className="grid grid-cols-2 gap-2 pt-1">
                <button onClick={() => setEditor({ ...EMPTY })} className="col-span-2 h-9 rounded-md bg-[#e52321] hover:bg-[#c51717] text-white text-xs font-bold inline-flex items-center justify-center gap-1.5" data-testid="mcu-pinout-add-btn"><Plus size={14} /> Add type</button>
                {sel && <>
                  <button onClick={() => setEditor({ ...sel })} className="h-9 rounded-md border border-zinc-700/70 text-xs text-zinc-300 hover:bg-white/5 inline-flex items-center justify-center gap-1.5" data-testid="mcu-pinout-edit-btn"><Pencil size={13} /> Edit</button>
                  <button onClick={del} className="h-9 rounded-md border border-red-900/60 text-xs text-red-400 hover:bg-red-950/40 inline-flex items-center justify-center gap-1.5" data-testid="mcu-pinout-delete-btn"><Trash2 size={13} /> Delete</button>
                </>}
              </div>
            )}
            <p className="text-[11px] text-zinc-500 leading-relaxed">Pinch / scroll / double-tap to zoom · drag to pan</p>
          </div>

          <div className="min-h-0 h-full flex flex-col" data-testid="mcu-pinout-detail">
            {editor ? <PinEditor initial={editor} onSaved={onSaved} onCancel={() => setEditor(null)} /> : sel ? (
              <>
                <div className="flex items-baseline gap-3 mb-2 px-1">
                  <span className="text-base font-black text-white" data-testid="mcu-pinout-detail-model">{sel.model}</span>
                  {sel.ecu && <span className="text-xs font-mono text-red-300">{sel.ecu}</span>}
                </div>
                {sel.image_url
                  ? <ZoomImage src={new URL(sel.image_url, api.defaults.baseURL).href} alt={sel.model} />
                  : <div className="rounded-md border border-dashed border-zinc-800 flex items-center justify-center text-xs text-zinc-600 h-40">No photo</div>}
              </>
            ) : (
              <div className="flex-1 min-h-[300px] rounded-md border border-dashed border-zinc-800 flex flex-col items-center justify-center gap-2 text-zinc-600" data-testid="mcu-pinout-empty">
                <Cable size={28} />
                <span className="text-xs">{items.length ? "Select a type to show the pin photo" : isAdmin ? "No data yet — click Add type" : "No pinout data yet"}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
